import React, { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// Custom video slide component
const VideoSlide = ({ src, title }) => {
  const getVideoEmbedUrl = (url) => {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0` : null;
    }
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.match(/vimeo\.com\/(?:[a-zA-Z0-9-_]+)?(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=1` : null;
    }
    // Direct video file
    return url;
  };

  const embedUrl = getVideoEmbedUrl(src);
  const isEmbed = embedUrl !== src;

  if (isEmbed) {
    return (
      <iframe
        src={embedUrl}
        title={title}
        style={{
          width: '90vw',
          height: '90vh',
          border: 'none',
          borderRadius: '8px',
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <video
      controls
      playsInline
      style={{
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '100%',
        height: 'auto',
        borderRadius: '8px',
      }}
    >
      <source src={src} />
      Your browser does not support the video tag.
    </video>
  );
};

function ImageLightbox({ open, setOpen, images, mediaTypes = [], currentIndex = 0 }) {
  const [index, setIndex] = useState(currentIndex);

  // Function to check if a URL is a video
  const isVideo = (url, idx) => {
    if (!url) return false;
    
    if (mediaTypes && mediaTypes[idx] === 'video') return true;
    
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const urlString = typeof url === 'string' ? url.toLowerCase() : '';
    
    if (videoExtensions.some(ext => urlString.includes(ext))) return true;
    
    if (urlString.includes('youtube.com') || 
        urlString.includes('youtu.be') || 
        urlString.includes('vimeo.com')) return true;
    
    return false;
  };

  // Custom slide renderer
const renderSlide = ({ slide, index: slideIndex }) => {
  const isVideoFile = isVideo(slide.src, slideIndex);

  // ✅ Custom render ONLY for videos
  if (isVideoFile) {
    return (
      <VideoSlide
        src={slide.src}
        title={slide.title || `Video ${slideIndex + 1}`}
      />
    );
  }

  // ❌ DO NOT render custom <img />
  // ✅ Let Lightbox handle images (required for zoom to work)
  return undefined;
};

  const slides = images.map((src, idx) => ({
    src: src,
    type: isVideo(src, idx) ? 'video' : 'image',
    title: `${isVideo(src, idx) ? 'Video' : 'Image'} ${idx + 1}`
  }));

  return (
    <Lightbox
      open={open}
      close={() => {
        setOpen(false);
        setIndex(0);
      }}
      slides={slides}
      index={index}
      on={{ view: ({ index: newIndex }) => setIndex(newIndex) }}
      plugins={[Zoom, Thumbnails]}
      render={{
        slide: renderSlide,
      }}
      zoom={{
        maxZoomPixelRatio: 3,
      }}
      thumbnails={{
        position: "bottom",
        hideWhenZoomed: false,
        width: 100,
        height: 70,
        gap: 10,
        borderRadius: 8,
        vignette: false,
      }}
      carousel={{
        finite: false,
        preload: 2,
      }}
      animation={{
        fade: 300,
        swipe: 500,
      }}
      styles={{
        container: {
          backgroundColor: "rgba(0, 0, 0, 0.96)",
        },
        button: {
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
          backgroundColor: "rgba(0,0,0,0.5)",
          borderRadius: "50%",
          margin: "0 10px",
        },
      }}
    />
  );
}

export default ImageLightbox;